/**
 * One place that decides whether two spellings are the same footballer.
 *
 * There were two. `NflChip.tsx` kept an alias table so the club chip could find "Nick Singleton"
 * in a player index that calls him Nicholas; nothing else knew, so the ADP board looked him up,
 * missed, and reported a mandatory farm graduate as having no market value at all. That is the
 * shape of bug this file exists to prevent: a name table that only one screen consults is a name
 * table that silently disagrees with every other screen.
 *
 * NORMALISING IS NOT ALIASING, and they are separate on purpose. Normalising is mechanical —
 * case, punctuation, spacing — and is safe to apply to anything. An alias is a CLAIM that two
 * different names are one man, which can be wrong, so each one is written down deliberately
 * rather than inferred from a similarity score. "Nick" and "Nicholas" are the same player here;
 * "Michael Carter" and "Michael Carter II" are famously not.
 */

/** Case, punctuation and spacing only. Never changes which man is being named. */
export const normName = (s: string): string =>
  s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim();

/**
 * Deliberate claims that two spellings are one player, keyed by normalised name.
 *
 * Add to this only when you have checked it is the same man. The cost of a wrong entry is a
 * keeper priced off somebody else's ranking, which nothing downstream can detect.
 */
export const NAME_ALIASES: Readonly<Record<string, string>> = {
  'nick singleton': 'nicholas singleton',
  'hollywood brown': 'marquise brown',
  'cadillac williams': 'carnell williams',
};

/**
 * The same name with the generational suffix removed.
 *
 * A LAST RESORT, never the primary key. Sources disagree about suffixes constantly — one file has
 * "Travis Etienne Jr." and the next has "Travis Etienne" — so something has to bridge them. But
 * the suffix is occasionally the only thing telling two men apart, so this is tried only after
 * the literal spelling and the declared aliases have both missed.
 */
export const looseName = (s: string): string =>
  normName(s).replace(/\b(jr|sr|ii|iii|iv|v)\b/g, '').replace(/\s+/g, ' ').trim();

/**
 * Every spelling worth trying for a name, most literal first.
 *
 * Order is the whole design: exact spelling, then a deliberate alias, then the suffix-stripped
 * form. A caller that stops at the first hit therefore prefers certainty to breadth.
 */
export function nameKeys(s: string): string[] {
  const k = normName(s);
  const out = [k];
  const alias = NAME_ALIASES[k];
  if (alias) out.push(alias);
  for (const [from, to] of Object.entries(NAME_ALIASES)) {
    if (to === k && !out.includes(from)) out.push(from);
  }
  const loose = looseName(s);
  if (!out.includes(loose)) out.push(loose);
  return out;
}

/**
 * Build a lookup that can be searched by any of a name's spellings.
 *
 * Literal keys are written first and never overwritten; loose keys fill in only where nothing has
 * claimed them. So "Michael Carter II" keeps his own entry, and a lookup for plain "Michael
 * Carter" still finds the man actually called that rather than being redirected to the second.
 */
export function indexByName<T>(items: readonly T[], nameOf: (t: T) => string): Map<string, T> {
  const map = new Map<string, T>();
  for (const it of items) map.set(normName(nameOf(it)), it);
  for (const it of items) {
    const loose = looseName(nameOf(it));
    if (loose && !map.has(loose)) map.set(loose, it);
  }
  return map;
}

/** Look a name up in a map built by `indexByName`, trying its spellings in order. */
export function lookupByName<T>(map: ReadonlyMap<string, T>, name: string): T | undefined {
  for (const k of nameKeys(name)) {
    const hit = map.get(k);
    if (hit !== undefined) return hit;
  }
  return undefined;
}
